import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/** Where emailed links should land. A native build has no web origin of its own
 *  (capacitor://localhost is not a URL Supabase or an email can point at), so
 *  it uses the real site — which, with universal links, opens the app. */
export const WEB_ORIGIN = 'https://readybreak.app';
export function authRedirect(): string {
  if (typeof location === 'undefined' || !/^https?:$/.test(location.protocol) || location.hostname === 'localhost') return WEB_ORIGIN;
  return location.origin;
}

/** Emailed-link verify types Supabase accepts with a token_hash. */
export type LinkType = 'email' | 'signup' | 'magiclink' | 'recovery' | 'invite' | 'email_change';
const LINK_TYPES: readonly string[] = ['email', 'signup', 'magiclink', 'recovery', 'invite', 'email_change'];

/**
 * An emailed link of the form `/auth/confirm?token_hash=…&type=…` (the email
 * templates point straight at readybreak.app, not at supabase.co, so that on
 * an iPhone the link is a universal link and opens the app). Returns null for
 * any other URL.
 */
export function parseConfirmLink(href: string): { tokenHash: string; type: LinkType } | null {
  let u: URL;
  try {
    u = new URL(href);
  } catch {
    return null;
  }
  if (!/\/auth\/confirm\/?$/.test(u.pathname)) return null;
  const tokenHash = u.searchParams.get('token_hash');
  const type = u.searchParams.get('type') ?? '';
  if (!tokenHash || !LINK_TYPES.includes(type)) return null;
  return { tokenHash, type: type as LinkType };
}

function readLanding(): { error: string | null; recovery: boolean; confirm: { tokenHash: string; type: LinkType } | null } {
  if (typeof location === 'undefined') return { error: null, recovery: false, confirm: null };
  const h = new URLSearchParams(location.hash.replace(/^#\/?/, ''));
  const q = new URLSearchParams(location.search);
  const error = h.get('error_description') || q.get('error_description');
  const confirm = parseConfirmLink(location.href);
  const recovery = (h.get('type') || q.get('type')) === 'recovery';
  if (error || confirm) {
    // Clear it so the router and a reload don't see it (a token_hash is single use).
    history.replaceState(null, '', '/');
  }
  return { error: error ? error.replace(/\+/g, ' ') : null, recovery, confirm };
}

/**
 * What an emailed auth link brought with it, read BEFORE the Supabase client
 * starts (it consumes and strips the tokens). Supabase reports a dead link
 * (expired, already used, opened on a different device) by redirecting here
 * with `#error_description=…`; earlier builds ignored that and just showed
 * the sign-in screen, so a failed link looked like "nothing happened".
 *
 * Declared after LINK_TYPES: it runs at import, so order matters.
 */
export const AUTH_LANDING = readLanding();

// ─── Invite join links ────────────────────────────────────────────────────
// An invite email links to `readybreak.app/?join=CODE`. The code is kept on
// this device until the coach has signed in (or created an account, which
// may take an email-confirmation round trip) and joined, then cleared.
const JOIN_KEY = 'rb_join_code';

/** The invite code in a `?join=` link, or null. */
export function parseJoinLink(href: string): string | null {
  try {
    const code = new URL(href).searchParams.get('join');
    return code && /^[A-Za-z0-9]{4,64}$/.test(code.trim()) ? code.trim().toUpperCase() : null;
  } catch {
    return null;
  }
}
export function getPendingJoin(): string | null {
  try {
    return localStorage.getItem(JOIN_KEY);
  } catch {
    return null;
  }
}
export function setPendingJoin(code: string | null): void {
  try {
    if (code) localStorage.setItem(JOIN_KEY, code);
    else localStorage.removeItem(JOIN_KEY);
  } catch {
    /* storage unavailable: the code is still shown in the email */
  }
}
(function readJoinLanding() {
  if (typeof location === 'undefined') return;
  const code = parseJoinLink(location.href);
  if (!code) return;
  setPendingJoin(code);
  const u = new URL(location.href);
  u.searchParams.delete('join');
  history.replaceState(null, '', u.pathname + u.search + u.hash);
})();

/** Where the invite email's join link points (the web app, which the native app also claims). */
export function joinUrl(code: string): string {
  return `${WEB_ORIGIN}/?join=${encodeURIComponent(code)}`;
}

const url = import.meta.env['VITE_SUPABASE_URL'] as string | undefined;
const anon = import.meta.env['VITE_SUPABASE_ANON_KEY'] as string | undefined;

/**
 * null when the build has no Supabase configured → the app runs in
 * "local" mode: single device, no accounts, data in IndexedDB only.
 * That mode exists so the app is usable (and demo-able) before a backend
 * exists, and as the fallback for a coach who never signs in.
 */
export const supabase: SupabaseClient | null = url && anon ? createClient(url, anon, { auth: { persistSession: true, autoRefreshToken: true } }) : null;

export const HAS_CLOUD = !!supabase;
